import { all, call, put, take, takeEvery, takeLatest } from 'redux-saga/effects'
import _ from 'lodash/fp'
import { types, actions } from '../reducers/guides'
import { getEffect, postEffect, putEffect, prependClubsUrl, getCurrentClubId } from './network'
import * as snack from './snackMessage'
import { SAVE_GUIDE_HOLES_MUTATION, GET_CLUB_GUIDES_QUERY, UPDATE_GUIDE_MUTATION, CREATE_GUIDE_MUTATION, COPY_GUIDE_MUTATION, DELETE_GUIDE_MUTATION, UPLOAD_GUIDE_ICON_MUTATION, RELEASE_DIGITAL_GUIDE_MUTATION, RELEASE_OVERVIEW_MUTATION, RELEASE_WEB_IMAGES_MUTATION, RELEASE_GREEN_DRAWINGS_MUTATION, RELEASE_TEE_SIGNS_MUTATION, GET_WEB_IMAGES_LOCATION_MUTATION, GET_TEE_SIGNS_LOCATION_MUTATION, GET_GREEN_DRAWINGS_LOCATION_MUTATION, GET_APP_ORDERS_QUERY, SET_APP_ORDERS_MUTATION } from './mutations/guides'
import { invalidateKioskWidgetsFlow } from './kioskWidgets'
import { apolloClient } from '@fe/business/src/apollo'

// Remove read-only DateTime and system fields that shouldn't be sent to legacy REST API
const removeReadOnlyFields = (guide) => {
  const readOnlyFields = [
    'generatedAtJson',
    'generatedAtPdf',
    'generatedAtOverview',
    'generatedAtTeesSigns',
    'generatedAtWebImages',
    'generatedAtGreenDrawings',
    'updatedAt',
    'deletedAt',
    'sysPeriod',
    'awsVersion'
  ]
  
  const filtered = _.omit(readOnlyFields, guide)
  
  // Also filter out created_at from extra_pages objects (we preserve ids though) so backend can distinguish updates from new uploads)
  if (filtered.extraPages && _.isArray(filtered.extraPages)) {
    filtered.extraPages = _.map(page => _.omit(['createdAt'], page), filtered.extraPages)
  }
  
  return filtered
}

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

export function* createGuideEffect(guide) {
  try {
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: CREATE_GUIDE_MUTATION,
      variables: { input: removeReadOnlyFields(guide) }
    })
    const created = _.get('data.GuidesMutation.createGuide', response)
    return created ?? { error: 'Guide not created' }
  } catch (error) {
    return { error }
  }
}

export function* copyGuideEffect(guideId, title) {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: COPY_GUIDE_MUTATION,
      variables: { clubId, guideId, input: { title } }
    })
    const copied = _.get('data.GuidesMutation.copyGuide', response)
    return copied ?? { error: 'Guide not copied' }
  } catch (error) {
    return { error }
  }
}

export function* deleteGuideEffect(guideId) {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: DELETE_GUIDE_MUTATION,
      variables: { clubId, guideId }
    })
    return _.get('data.GuidesMutation.deleteGuide', response)
  } catch (error) {
    return { error }
  }
}

export function* setVisibilityEffect(guideId, visible) {
  return yield apolloClient.mutate({
    mutation: SET_GUIDE_VISIBILITY_MUTATION,
    variables: {
      input: {
        guideId,
        visible
      }
    }
  })
}

export function* generateDigitalEffect(guide, sideEffects = []) {
  const { guideId, clubId } = guide
  const response = yield call([apolloClient, apolloClient.mutate], {
    mutation: RELEASE_DIGITAL_GUIDE_MUTATION,
    variables: { clubId, guideId, input: {} }
  })

  const success = _.get('data.GuidesMutation.releaseDigitalGuide', response)
  return success ? {} : { error: 'Release failed' }
}

export function* generateOverviewEffect(guide) {
  const { guideId, clubId } = guide
  const response = yield call([apolloClient, apolloClient.mutate], {
    mutation: RELEASE_OVERVIEW_MUTATION,
    variables: { clubId, guideId }
  })

  const success = _.get('data.GuidesMutation.releaseOverview', response)
  return success ? {} : { error: 'Release failed' }
}

export function* generateWebImagesEffect(guideId) {
  const clubId = yield call(getCurrentClubId)
  const response = yield call([apolloClient, apolloClient.mutate], {
    mutation: RELEASE_WEB_IMAGES_MUTATION,
    variables: { clubId, guideId }
  })

  const success = _.get('data.GuidesMutation.releaseWebImages', response)
  return success ? {} : { error: 'Release failed' }
}

export function* generateWebImagesLocationEffect(guideId) {
  const clubId = yield call(getCurrentClubId)
  const { data } = yield apolloClient.mutate({
    mutation: GET_WEB_IMAGES_LOCATION_MUTATION,
    variables: { clubId, guideId }
  })
  return { location: data.GuidesMutation.getWebImagesLocation }
}

export function* generateGreenDrawingsEffect(guideId) {
  const clubId = yield call(getCurrentClubId)
  const response = yield call([apolloClient, apolloClient.mutate], {
    mutation: RELEASE_GREEN_DRAWINGS_MUTATION,
    variables: { clubId, guideId }
  })

  const success = _.get('data.GuidesMutation.releaseGreenDrawings', response)
  return success ? {} : { error: 'Release failed' }
}

export function* generateGreenDrawingsLocationEffect(guideId) {
  const clubId = yield call(getCurrentClubId)
  const { data } = yield apolloClient.mutate({
    mutation: GET_GREEN_DRAWINGS_LOCATION_MUTATION,
    variables: { clubId, guideId }
  })
  return { location: data.GuidesMutation.getGreenDrawingsLocation }
}

// Legacy endpoint on purpose: the GraphQL `releasePdf` only renders images, the real PDF assembly is not ported yet
export function* generatePdfEffect(guide) {
  const { guideId } = guide
  const request = {
    url: (yield call(prependClubsUrl, `guides/${guideId}/generate_pdf`))
  }
  const chan = yield call(postEffect, request)
  return yield take(chan)
}

export function* generateTeeSignsEffect(guide) {
  const { guideId } = guide
  const clubId = yield call(getCurrentClubId)
  const response = yield call([apolloClient, apolloClient.mutate], {
    mutation: RELEASE_TEE_SIGNS_MUTATION,
    variables: { clubId, guideId }
  })

  const success = _.get('data.GuidesMutation.releaseTeeSigns', response)
  return success ? {} : { error: 'Release failed' }
}

export function* generateTeeSignsLocationEffect(guideId) {
  const clubId = yield call(getCurrentClubId)
  const { data } = yield apolloClient.mutate({
    mutation: GET_TEE_SIGNS_LOCATION_MUTATION,
    variables: { clubId, guideId }
  })
  return { location: data.GuidesMutation.getTeeSignsLocation }
}

export function* loadGuidesEffect(payload) {
  const payloadClubId = _.get('clubId', payload)
  const clubId = payloadClubId || (yield call(getCurrentClubId))

  try {
    const result = yield call([apolloClient, apolloClient.query], {
      query: GET_CLUB_GUIDES_QUERY,
      variables: { clubId },
      fetchPolicy: 'network-only'
    })

    return result?.data?.GuidesQuery?.clubGuides || []
  } catch (error) {
    return { error }
  }
}

export function* saveGuideEffect(guide) {
  const clubId = yield call(getCurrentClubId)

  try {
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: UPDATE_GUIDE_MUTATION,
      // The whole guide is sent on every save, matching the legacy PUT; splitting this into
      // targeted mutations is deliberately deferred.
      variables: { input: { ...removeReadOnlyFields(guide), clubId } }
    })
    const updated = _.get('data.GuidesMutation.updateGuide', response)
    // Resolver returns null when the guide no longer exists; callers only branch on `.error`
    return updated ?? { error: 'Guide not found' }
  } catch (error) {
    return { error }
  }
}

export function* uploadLogoEffect(logoData) {
  const { guideId, clubId, data } = logoData
  try {
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: UPLOAD_GUIDE_ICON_MUTATION,
      variables: { clubId, guideId, input: { data } }
    })
    const success = _.get('data.GuidesMutation.uploadGuideIcon', response)
    return success ? {} : { error: 'Upload failed' }
  } catch (error) {
    return { error }
  }
}

function* loadAppOrdersEffect() {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.query], {
      query: GET_APP_ORDERS_QUERY,
      variables: { clubId },
      fetchPolicy: 'network-only'
    })
    return { orders: _.get('data.GuidesQuery.appOrders', response) }
  } catch (error) {
    return { error }
  }
}

function* setAppOrdersEffect(orders) {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: SET_APP_ORDERS_MUTATION,
      variables: { clubId, orders }
    })
    return { success: _.get('data.GuidesMutation.setAppOrders', response) }
  } catch (error) {
    return { error }
  }
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

// payload: { guideId, title }
export function* copyGuideFlow({ payload }) {
  const { guideId, title } = payload

  const res = yield call(copyGuideEffect, guideId, title)

  if (res.error) {
    yield put(actions.copyError(res))
  } else {
    // fire up a load request to get the new guide
    yield put(actions.loadRequest())
    // when the success is put we don't yet have the new guide, but that's ok
    yield put(actions.copySuccess(res))
  }

  yield put(actions.copyDone())
}

export function* deleteGuideFlow({ payload: guideId }) {
  const response = yield call(deleteGuideEffect, guideId)
  const error = _.get('error', response)

  if (error) {
    yield put(actions.deleteError(error))
  } else {
    yield put(actions.deleteSuccess(guideId))
  }

  yield put(actions.deleteDone())
}

export function* setVisibilityFlow({ payload: { guideId, visible } }) {
  try {
    yield call(setVisibilityEffect, guideId, visible)
    yield put(actions.setVisibilitySuccess({ guideId, visible }))
    const clubId = yield call(getCurrentClubId)
    yield put(actions.loadRequest({ clubId }))
  } catch (error) {
    yield put(actions.setVisibilityError({ error }))
  }

  yield put(actions.setVisibilityDone())
}

export function* generateDigitalFlow({ payload }) {
  yield snack.setMessage('Generating GLFR Guide')

  const { guide, sideEffects } = payload

  if (guide.guideId) {
    const res = yield call(generateDigitalEffect, guide, sideEffects)

    if (res.error) {
      yield put(actions.generateDigitalError({ error: res.error }))
    } else {
      yield put(actions.generateDigitalSuccess({ guide }))

      yield call(invalidateKioskWidgetsFlow, {
        clubId: _.get('clubId', guide),
        widgetNames: ['course-guide', 'handicap-calculator', 'overview-map']
      })
    }
  }

  yield put(actions.generateDigitalDone({ guide }))
}

export function* generateOverviewFlow({ payload }) {
  yield snack.setMessage('Generating Overview')

  const { guide } = payload

  if (guide.guideId) {
    const res = yield call(generateOverviewEffect, guide)

    if (res.error) {
      yield put(actions.generateOverviewError({ error: res.error }))
    } else {
      yield put(actions.generateOverviewSuccess({ guide }))
    }
  }

  yield put(actions.generateOverviewDone({ guide }))
}

export function* generatePdfFlow({ payload }) {
  yield snack.setMessage('Generating PDF Guide')

  const { guide } = payload

  if (guide.guideId) {
    const res = yield call(generatePdfEffect, guide)

    if (res.error) {
      yield put(actions.generatePdfError({ error: res.error }))
    } else {
      yield put(actions.generatePdfSuccess({ guide }))
    }
  }

  yield put(actions.generatePdfDone({ guide }))
}

export function* generateTeeSignsFlow({ payload }) {
  yield snack.setMessage('Generating Tee Signs')

  const { guide } = payload
  const { guideId } = guide

  if (guideId) {
    const res = yield call(generateTeeSignsEffect, guide)

    if (res.error) {
      yield put(actions.generateTeeSignsError({ error: res.error }))
    } else {
      yield put(actions.generateTeeSignsSuccess({ guide }))
    }
  }

  yield put(actions.generateTeeSignsDone({ guide }))
}

export function* generateTeeSignsUrlFlow({ payload: guideId }) {
  const { location: url } = yield call(generateTeeSignsLocationEffect, guideId)
  yield put(actions.setTeeSignsUrl({ guideId, url }))
}

export function* reloadGuidesFlow({ payload }) {
  const clubId = _.get('guide.clubId', payload)
  return yield call(loadGuidesFlow, { payload: { clubId } })
}

export function* loadGuidesFlow({ payload }) {
  const guides = yield call(loadGuidesEffect, payload)
  yield put(actions.loadRequestDone())

  if (guides.error) {
    yield put(actions.loadError(guides))
  } else {
    yield put(actions.loadSuccess(guides))
  }
}

export function* saveGuideRedirectFlow({ payload: { guide } }) {
  const res = yield call(saveGuideEffect, guide)

  if (res.error) {
    yield put(actions.saveError({ error: res.error }))
  } else {
    yield put(actions.saveSuccessRedirect(res))
  }

  yield put(actions.saveDone())
}

export function* saveGuideFlow({ payload: guide }) {
  if (!guide.guideId) {
    const res = yield call(createGuideEffect, guide)

    if (res.error) {
      yield put(actions.saveError({ error: res.error }))
    } else {
      yield put(actions.saveSuccessRedirect(res))
    }
  } else {
    const res = yield call(saveGuideEffect, guide)

    if (res.error) {
      yield put(actions.saveError({ error: res.error }))
    } else {
      yield put(actions.saveSuccess(res))
    }
  }
  yield put(actions.loadRequest({ clubId: _.get('clubId', guide) }))
  yield take([types.LOAD_SUCCESS, types.LOAD_ERROR])

  yield put(actions.saveDone())
}

export function* uploadLogoFlow({ payload }) {
  const res = yield call(uploadLogoEffect, payload)

  if (res.error) {
    yield put(actions.uploadLogoError({ error: res.error }))
  } else {
    yield put(actions.uploadLogoSuccess(res))
  }

  yield put(actions.loadRequest({ clubId: _.get('clubId', payload) }))

  yield put(actions.uploadLogoDone())
}

export function* generateWebImagesFlow({ payload: guideId }) {
  yield snack.setMessage('Generating Web Images')
  yield call(generateWebImagesEffect, guideId)
}

export function* generateWebImagesUrlFlow({ payload: guideId }) {
  const { location: url } = yield call(generateWebImagesLocationEffect, guideId)
  yield put(actions.generateWebImagesUrlSet({ guideId, url }))
}

export function* generateGreenDrawingsFlow({ payload: guideId }) {
  yield snack.setMessage('Generating Green Drawings')
  yield call(generateGreenDrawingsEffect, guideId)
}

export function* generateGreenDrawingsUrlFlow({ payload: guideId }) {
  const { location: url } = yield call(generateGreenDrawingsLocationEffect, guideId)
  yield put(actions.generateGreenDrawingsUrlSet({ guideId, url }))
}

function* loadAppOrdersFlow() {
  const res = yield call(loadAppOrdersEffect)
  if (res.error) {
    console.warn(res.error)
  }
  yield put(actions.loadAppOrdersDone(res))
}

function* setAppOrdersFlow({ payload }) {
  const res = yield call(setAppOrdersEffect, payload)
  const clubId = yield call(getCurrentClubId)
  if (res.error) {
    console.warn(res.error)
    yield put(actions.setAppOrdersFailure(res.error))
  } else {
    yield call(loadGuidesFlow, { payload: { clubId } })
  }
  yield put(actions.setAppOrdersDone())
}

function* saveGuideHolesFlow({ payload }) {
  const { guideId, guideHoles } = payload;

  try {
    yield apolloClient.mutate({
      mutation: SAVE_GUIDE_HOLES_MUTATION,
      variables: {
        guideId,
        saveGuideHolesInput: guideHoles,
      },
    });

    const guides = yield call(loadGuidesEffect);
    yield put(actions.loadRequestDone());
    yield put(actions.saveGuideHolesDone());
    yield put(actions.loadSuccess(guides));
  } catch (error) {
    yield put(actions.saveGuideHolesDone());
    console.error("saveGuideHolesFlow error", error);
  }
}

export default function*() {
  return yield all([
    takeEvery(types.COPY, copyGuideFlow),
    takeEvery(types.DELETE, deleteGuideFlow),
    takeEvery(types.SET_VISIBILITY, setVisibilityFlow),
    takeEvery(types.GENERATE_DIGITAL, generateDigitalFlow),
    takeEvery(types.GENERATE_OVERVIEW, generateOverviewFlow),
    takeEvery(types.GENERATE_PDF, generatePdfFlow),
    takeEvery(types.GENERATE_TEE_SIGNS, generateTeeSignsFlow),
    takeLatest(types.GENERATE_TEE_SIGNS_URL, generateTeeSignsUrlFlow),
    takeEvery(types.GENERATE_DIGITAL_SUCCESS, reloadGuidesFlow),
    takeEvery(types.GENERATE_OVERVIEW_SUCCESS, reloadGuidesFlow),
    takeEvery(types.GENERATE_PDF_SUCCESS, reloadGuidesFlow),
    takeEvery(types.GENERATE_TEE_SIGNS_SUCCESS, reloadGuidesFlow),
    takeEvery(types.GENERATE_WEB_IMAGES, generateWebImagesFlow),
    takeEvery(types.GENERATE_WEB_IMAGES_URL_REQUEST, generateWebImagesUrlFlow),
    takeEvery(types.GENERATE_GREEN_DRAWINGS, generateGreenDrawingsFlow),
    takeEvery(types.GENERATE_GREEN_DRAWINGS_URL_REQUEST, generateGreenDrawingsUrlFlow),
    takeLatest(types.LOAD_REQUEST, loadGuidesFlow),
    takeEvery(types.SAVE, saveGuideFlow),
    takeEvery(types.SAVE_GUIDE_HOLES, saveGuideHolesFlow),
    takeEvery(types.SAVE_REDIRECT, saveGuideRedirectFlow),
    takeEvery(types.UPLOAD_LOGO, uploadLogoFlow),

    takeLatest(types.LOAD_APP_ORDERS, loadAppOrdersFlow),
    takeLatest(types.SET_APP_ORDERS, setAppOrdersFlow)
  ])
}
